import { registerForDemonstration } from "api/demonstration";
import Terms from "components/Terms";
import { Formik, Form as FormikForm, FormikProps } from "formik";
import useGoogleRecaptcha from "hooks/useGoogleRecaptcha";
import { Button, errorToast, warningToast } from "iparque-components";
import { AppContext } from "providers/AppProvider";
import { useMessageContext } from "providers/MessageProvider";
import React, { useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import RequiredFieldsMessage from "components/RequiredFields/RequiredFieldsMessage";
import { MessageFormValues, initialValues, validationSchema } from "../configs";
import {
  ButtonsContainer,
  Container,
  InputRow,
  StyledInput,
  StyledTextArea,
} from "./styles";

interface Props {
  className?: string;
  onCancel: () => void;
  onSuccess: () => void;
  productId: number;
}

const MessageForm = React.forwardRef<HTMLDivElement, Props>(
  ({ className, onCancel, onSuccess, productId }, ref) => {
    const { t } = useTranslation();
    const { setIsLoading } = useContext(AppContext);
    const { isFormVisible } = useMessageContext();
    const formRef = useRef<FormikProps<MessageFormValues>>(null);
    const { executeGoogleRecaptcha, isGoogleRecaptchaLoaded } =
      useGoogleRecaptcha();

    const onCancelHandler = () => {
      formRef.current?.resetForm();
      onCancel();
    };

    const onSubmit = async (values: MessageFormValues) => {
      setIsLoading(true);
      const token = await executeGoogleRecaptcha();

      if (!token) {
        setIsLoading(false);
        warningToast(
          t("7857") /* Aviso! */,
          t("7858") /* Por favor, tenta novamente. */
        );
        return;
      }

      const registerForDemonstrationResult = await registerForDemonstration({
        email: values.email,
        name: values.name,
        phone: values.phone,
        message: values.message,
        recaptchaToken: token,
        productId,
      });

      if (!registerForDemonstrationResult.success) {
        setIsLoading(false);
        errorToast(t("7847") /* Erro! */, t("3402") /* Ocorreu um erro */);
        return;
      }

      formRef.current?.resetForm();
      onSuccess();

      setIsLoading(false);
    };

    return (
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formikProps) => {
          const hasErrors =
            formikProps.touched &&
            Object.keys(formikProps.errors).length > 0 &&
            Object.keys(formikProps.touched).length > 0;

          return (
            <FormikForm>
              <Container
                ref={ref}
                isVisible={isFormVisible}
                className={className}
              >
                <StyledInput
                  id="enforcer-message-name"
                  name="name"
                  placeholder={t("176") /* Nome */}
                  label={t("176") /* Nome */}
                  required
                />
                <InputRow>
                  <StyledInput
                    id="enforcer-message-email"
                    name="email"
                    placeholder={t("178") /* Email */}
                    label={t("178") /* Email */}
                    required
                  />
                  <StyledInput
                    id="enforcer-message-phone"
                    name="phone"
                    required
                    placeholder={t("10179") /* Contacto telefónico */}
                    label={t("10179") /* Contacto telefónico */}
                  />
                </InputRow>
                <StyledTextArea
                  id="enforcer-message-message"
                  name="message"
                  placeholder={t("1990") /* Mensagem */}
                  label={t("1990") /* Mensagem */}
                  rows={3}
                  required
                />
                <div>
                  <Terms />
                  <RequiredFieldsMessage hasErrors={hasErrors} />
                </div>
                <ButtonsContainer>
                  <Button
                    type="button"
                    color="secondary"
                    variant="outlined"
                    onClick={onCancelHandler}
                  >
                    {t("48") /* Cancelar */}
                  </Button>
                  <Button
                    type="submit"
                    color="secondary"
                    disabled={!isGoogleRecaptchaLoaded}
                  >
                    {t("1989") /* Enviar */}
                  </Button>
                </ButtonsContainer>
              </Container>
            </FormikForm>
          );
        }}
      </Formik>
    );
  }
);

export default MessageForm;
